import React, { useState, useEffect } from 'react';
import {
  resolveRegistrationFields, saveRegistrationForm, CUSTOM_FIELD_TYPES,
} from '../../../api/eventsApi';

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 30);

const EMPTY_CUSTOM = { label: '', type: 'text', required: false, options: '' };

/**
 * Lets an admin choose which fields the public registration form for an event collects.
 * The default fields can be switched off / made optional (name and email are always on);
 * extra custom questions can be added.
 */
export default function RegistrationFormBuilder({ orgId, event, onSaved }) {
  const [fields, setFields] = useState(() => resolveRegistrationFields(event));
  const [draft, setDraft]   = useState(EMPTY_CUSTOM);
  const [draftErr, setDraftErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState(null); // { type: 'ok' | 'err', text }

  useEffect(() => { setFields(resolveRegistrationFields(event)); setMsg(null); }, [event?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (key, changes) => setFields(fs => fs.map(f => (f.key === key ? { ...f, ...changes } : f)));

  const addCustom = () => {
    const label = draft.label.trim();
    if (!label) { setDraftErr('Give the question a label.'); return; }
    const key = `custom_${slug(label)}`;
    if (key === 'custom_' || fields.some(f => f.key === key)) { setDraftErr('A question with this label already exists.'); return; }
    const options = draft.options.split(',').map(o => o.trim()).filter(Boolean);
    if (draft.type === 'select' && options.length < 2) { setDraftErr('A dropdown needs at least two comma-separated options.'); return; }
    setFields(fs => [...fs, {
      key, label, type: draft.type, enabled: true, required: draft.required, custom: true,
      ...(draft.type === 'select' ? { options } : {}),
    }]);
    setDraft(EMPTY_CUSTOM);
    setDraftErr('');
  };

  const removeCustom = (key) => setFields(fs => fs.filter(f => f.key !== key));

  const phoneOff = !fields.find(f => f.key === 'phone')?.enabled;
  const subsOn = fields.find(f => f.key === 'subscriptions')?.enabled;

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      // Persist only what the backend needs to rebuild the form.
      const payload = fields.map(({ key, label, type, enabled, required, custom, options }) => ({
        key, label, type, enabled, required,
        ...(custom ? { custom: true } : {}),
        ...(options ? { options } : {}),
      }));
      await saveRegistrationForm(orgId, event.id, payload);
      setMsg({ type: 'ok', text: 'Registration form saved.' });
      onSaved && onSaved(event.id, payload);
    } catch (e) {
      setMsg({ type: 'err', text: e.message || 'Failed to save the registration form.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #DDD6FE', borderRadius: 8, padding: 16, marginTop: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: '#5B21B6', marginBottom: 2 }}>Registration Form Fields</div>
      <p style={{ margin: '0 0 12px', fontSize: 12.5, color: '#6B7280' }}>
        Choose what people are asked when they register via the link / QR code above. Everyone who registers is saved as a
        member record with the answers they give.
      </p>

      <table className="bb-table" style={{ marginBottom: 12 }}>
        <thead>
          <tr><th>Field</th><th style={{ width: 90 }}>Show</th><th style={{ width: 110 }}>Required</th><th style={{ width: 70 }} /></tr>
        </thead>
        <tbody>
          {fields.map(f => (
            <tr key={f.key}>
              <td>
                {f.label}
                {f.custom && <span className="bb-badge bb-badge--grey" style={{ marginLeft: 8 }}>Custom · {CUSTOM_FIELD_TYPES.find(t => t.value === f.type)?.label}</span>}
                {f.locked && <span style={{ marginLeft: 8, fontSize: 11, color: '#9CA3AF' }}>always on</span>}
                {f.type === 'nric4' && <div style={{ fontSize: 11, color: '#9CA3AF' }}>3 digits + letter, e.g. 567A. Never ask for the full NRIC.</div>}
              </td>
              <td>
                <input type="checkbox" checked={f.enabled} disabled={f.locked}
                  aria-label={`Show ${f.label}`} onChange={e => patch(f.key, { enabled: e.target.checked })} />
              </td>
              <td>
                <input type="checkbox" checked={f.enabled && f.required} disabled={f.locked || !f.enabled || f.type === 'subscriptions'}
                  aria-label={`${f.label} required`} onChange={e => patch(f.key, { required: e.target.checked })} />
              </td>
              <td>
                {f.custom && <button type="button" className="bb-btn-danger-sm" onClick={() => removeCustom(f.key)}>Remove</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {subsOn && phoneOff && (
        <div className="bb-alert bb-alert--error" style={{ marginBottom: 12 }}>
          Telephone is hidden, so people cannot subscribe to WhatsApp (a number is needed to message them).
        </div>
      )}

      <div style={{ background: '#F9FAFB', border: '1px dashed #D1D5DB', borderRadius: 8, padding: 12, marginBottom: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Add a custom question</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '2 1 200px' }}>
            <label style={{ fontSize: 12 }}>Label</label>
            <input className="bb-input" value={draft.label} placeholder="e.g. Dietary requirements"
              onChange={e => setDraft(d => ({ ...d, label: e.target.value }))} maxLength={120} />
          </div>
          <div style={{ flex: '1 1 130px' }}>
            <label style={{ fontSize: 12 }}>Type</label>
            <select className="bb-select" value={draft.type} onChange={e => setDraft(d => ({ ...d, type: e.target.value }))}>
              {CUSTOM_FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {draft.type === 'select' && (
            <div style={{ flex: '2 1 200px' }}>
              <label style={{ fontSize: 12 }}>Options (comma-separated)</label>
              <input className="bb-input" value={draft.options} placeholder="Vegetarian, Halal, None"
                onChange={e => setDraft(d => ({ ...d, options: e.target.value }))} />
            </div>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 8 }}>
            <input type="checkbox" checked={draft.required} onChange={e => setDraft(d => ({ ...d, required: e.target.checked }))} /> Required
          </label>
          <button type="button" className="bb-btn-outline-sm" onClick={addCustom}>+ Add</button>
        </div>
        {draftErr && <div style={{ color: '#B91C1C', fontSize: 12, marginTop: 6 }}>{draftErr}</div>}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button type="button" className="bb-btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save Form Fields'}
        </button>
        {msg && <span style={{ fontSize: 13, color: msg.type === 'ok' ? '#166534' : '#B91C1C' }}>{msg.text}</span>}
      </div>
    </div>
  );
}
